import type { ScriptElement } from '../schema';
import { CalloutElement } from './elements/CalloutElement';
import { ChipElement } from './elements/ChipElement';
import { EmbedElement } from './elements/EmbedElement';
import { IconElement } from './elements/IconElement';
import { ImageElement } from './elements/ImageElement';
import { LatexElement } from './elements/LatexElement';
import { McqElement, type McqInteractive } from './elements/McqElement';
import { MermaidElement } from './elements/MermaidElement';
import { ShapeElement } from './elements/ShapeElement';
import { TableElement } from './elements/TableElement';
import { TextElement } from './elements/TextElement';
import { ThreeDElement } from './elements/ThreeDElement';
import { TimelineElement } from './elements/TimelineElement';
import { VideoElement } from './elements/VideoElement';

/**
 * Dispatcher: hand an authored element to the correct renderer. `fill` is true
 * when the parent wrapper sets an explicit width/height (freeform mode) so the
 * child should stretch to fill.
 */
export function StageElement({
  element,
  fill,
  quiz,
  writing,
  writeMs,
  onWritingDone,
  paused,
  hideMcq,
}: {
  element: ScriptElement;
  fill?: boolean;
  quiz?: McqInteractive;
  /** Text is streaming in character-by-character (handwriting reveal). */
  writing?: boolean;
  /** Target duration (ms) for the write-on, synced to the teacher's pace. */
  writeMs?: number;
  /** Fired when a writing text element finishes its character stream. */
  onWritingDone?: () => void;
  /** Freeze the handwriting stream in place (mirrors the player's pause). */
  paused?: boolean;
  /** Suppress board MCQ (it's answered in the bottom overlay instead). */
  hideMcq?: boolean;
}) {
  switch (element.type) {
    case 'text':
      return (
        <TextElement
          element={element}
          fill={fill}
          writing={writing}
          writeMs={writeMs}
          onWritingDone={onWritingDone}
          paused={paused}
        />
      );
    case 'image':
      return <ImageElement element={element} fill={fill} />;
    case 'mcq':
      return hideMcq ? null : <McqElement element={element} quiz={quiz} />;
    case 'icon':
      return <IconElement element={element} fill={fill} />;
    case 'callout':
      return <CalloutElement element={element} fill={fill} />;
    case 'mermaid':
      return <MermaidElement element={element} />;
    case 'timeline':
      return <TimelineElement element={element} fill={fill} />;
    case 'table':
      return <TableElement element={element} fill={fill} />;
    case 'shape':
      return <ShapeElement element={element} fill={fill} />;
    case 'chip':
      return <ChipElement element={element} />;
    case 'latex':
      return (
        <LatexElement
          element={element}
          fill={fill}
          writing={writing}
          writeMs={writeMs}
          onWritingDone={onWritingDone}
          paused={paused}
        />
      );
    case 'video':
      return <VideoElement element={element} fill={fill} />;
    case 'embed':
      return <EmbedElement element={element} />;
    case '3d':
      return <ThreeDElement element={element} fill={fill} />;
    // Unknown / future element types render NOTHING (never throw).
    default:
      return null;
  }
}
